import { TranslatedSelectWithSearchFormFeature } from '../../translated-components';
import { useDefaultCountriesByLang } from '../../shared/hooks/useDefaultCountriesByLang';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { updateUserPersonalData } from '../../api/user';
import { setUser } from '../../redux/user/reducer';
import useToast from '../../context/ToastContext/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18nCountries from 'i18n-iso-countries';
import { userSelector } from '../../redux/user/selector';
import { useIonViewDidEnter } from '@ionic/react';
import { USAStates, useAnalytics } from 'src/shared';
import { isUserHaveUnitedStatesCountry } from 'src/shared/utils/isUserHaveUnitedStatesCountry';

type ProfileCountryOfLivingWidgetProps = {
  onSuccess?: (country: string) => void;
};

export function ProfileCountryOfLivingWidget({ onSuccess }: ProfileCountryOfLivingWidgetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const onSuccessCbRef = useRef(onSuccess);
  const defaultCountries = useDefaultCountriesByLang();
  const user = useSelector(userSelector);
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const {
    t,
    i18n: { language },
  } = useTranslation(['profile', 'auth']);
  const { trackPredefinedEvent } = useAnalytics();

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'country',
    });
  });

  const countries = Object.entries(i18nCountries.getNames('en', { select: 'official' })).map(([_, name]) => name);

  const defaultCountriesList = useMemo(
    () =>
      user?.country && defaultCountries.find((c) => c === user?.country) === undefined
        ? [user?.country, ...defaultCountries]
        : defaultCountries,
    [defaultCountries, user?.country]
  );

  useLayoutEffect(() => {
    onSuccessCbRef.current = onSuccess;
  });

  const handleSubmit = useCallback(
    async (countryOrState: string) => {
      try {
        setIsLoading(true);

        let updateObject = {};
        if (language === 'en' && USAStates?.includes(countryOrState)) {
          const unitedStatesName = i18nCountries.getName('US', 'en');
          updateObject = { country: unitedStatesName, us_state: countryOrState };
        } else {
          updateObject = { country: countryOrState, us_state: null };
        }

        const { data, error, message } = await updateUserPersonalData(updateObject);

        if (error) {
          throw new Error(message ? t(message) : 'Something went wrong');
        }

        trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
          screen_type: 'country',
          value: data.country,
          need_to_update: true,
        });

        dispatch(setUser(data));
        onSuccessCbRef.current?.(data.country);
      } catch (e: any) {
        showToast({ message: e?.message });
      } finally {
        setIsLoading(false);
      }
    },
    [onSuccessCbRef]
  );

  const selectedItems = useMemo(() => {
    if (!user?.us_state && !user?.country) {
      return new Set([]);
    }

    const country = isUserHaveUnitedStatesCountry(user?.country ?? '')
      ? user?.us_state ?? user?.country ?? ''
      : user?.country ?? '';

    return new Set([country]);
  }, [user?.country, user?.us_state, user?.app_lang]);

  return (
    <>
      <TranslatedSelectWithSearchFormFeature
        title={t('profile:country_of_living.title')}
        subTitle={t('profile:country_of_living.description')}
        buttonLabel={t('profile:country_of_living.button')}
        items={countries.filter((name) => (language === 'en' ? name !== 'United States of America' : true))}
        selectedItems={selectedItems}
        onChange={handleSubmit}
        isLoading={isLoading}
        defaultItems={defaultCountriesList.filter((name) =>
          language === 'en' ? name !== 'United States of America' : true
        )}
        otherText={t('auth:auth.country_of_living.other')}
        enableSearch={language === 'en'}
        searchPlaceholder={'Search'}
      />
    </>
  );
}
